/**
=========================================================
* Material Dashboard 2 PRO React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-pro-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

// @material-ui core components
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import Autocomplete from "@mui/material/Autocomplete";

// Material Dashboard 2 PRO React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";

// Settings page components
import FormField from "layouts/pages/account/components/FormField";

// Data
import MDAvatar from "../../../../../../components/MDAvatar";
import FacebookIcon from "@mui/icons-material/Facebook";
import { InputAdornment, TextField } from "@mui/material";
import { Instagram, Twitter } from "@mui/icons-material";
import MDButton from "../../../../../../components/MDButton";
import { useState } from "react";
import MDEditor from "../../../../../../components/MDEditor";
import { useOrganizationUpdateMutation } from "../../../../../../store/api/account";
import { notificationActions } from "../../../../../../store/notification/notification-slice";
import { useDispatch } from "react-redux";
import IconButton from "@mui/material/IconButton";
import Badge from "@mui/material/Badge";
import Icon from "@mui/material/Icon";
import {SERVER} from "../../../../../../helpers/constants/url";

function OrganizationInfo({organization}) {
  const [organizationUpdate] = useOrganizationUpdateMutation()

  const [isEdit, setIsEdit] = useState(false);

  const [organizationName, setOrganizationName] = useState(organization.name);
  const [organizationLogo, setOrganizationLogo] = useState(SERVER + organization.logo);
  const [organizationLogoUpload, setOrganizationLogoUpload] = useState();
  const [description, setDescription] = useState(organization.description);
  const [facebook, setFacebook] = useState(organization.facebook);
  const [instagram, setInstagram] = useState(organization.instagram);
  const [twitter, setTwitter] = useState(organization.twitter);
  const dispatch = useDispatch();

  const handleSave = async (e) => {
    setIsEdit(false);
    e.preventDefault();
    const organizationData = {
      owner: organization.owner,
      name: organizationName,
      logo: organizationLogoUpload,
      description,
      facebook,
      twitter,
      instagram
    };
    const save_organization = new FormData();
    save_organization.append("owner", organization.owner);
    organizationName && save_organization.append("name", organizationName);
    organizationLogoUpload && save_organization.append("logo", organizationLogoUpload);
    description && save_organization.append("description", description);
    facebook && save_organization.append("facebook", facebook);
    twitter && save_organization.append("twitter", twitter);
    instagram && save_organization.append("instagram", instagram);

    dispatch(notificationActions.showLoading(true));
    try {
      const payload = await organizationUpdate(save_organization);
      if(payload.error) {
        dispatch(notificationActions.showMessage({message: "An error occured", variant: 'error'}));
      } else {
        dispatch(notificationActions.showMessage({message: "Update successfully", variant: 'success'}));
      }
    }catch (e) {
      dispatch(notificationActions.showMessage({message: e.data.detail, variant: 'error'}));
    }
    dispatch(notificationActions.showLoading(false));
  }
  const handleEditCancel = (e) => {
    if(isEdit){
      setOrganizationName(organization.name);
      setOrganizationLogo(organization.logo);
      setDescription(organization.description);
      setFacebook(organization.facebook);
      setTwitter(organization.twitter);
      setInstagram(organization.instagram);
      setOrganizationLogoUpload(null);
    }
    setIsEdit(!isEdit);
  }
  const handleLogo = (e) => {
    if(e.target.files.length === 0){
      return;
    }
    const maxAllowedSize = 2 * 1024 * 1024;
    if (e.target.files[0].size > maxAllowedSize) {
      dispatch(notificationActions.showMessage({message: "File is too big (max 2MB)", variant: 'error'}));
    } else {
    setOrganizationLogoUpload(e.target.files[0]);
    const url = URL.createObjectURL(e.target.files[0]);
    setOrganizationLogo(url);
    }
  }

  return (
    <Card id="organization" sx={{ overflow: "visible" }}>
      <MDBox p={3}>
        <MDTypography variant="h5">Organization Info</MDTypography>
      </MDBox>
      <MDBox component="form" pb={3} px={3}>
        <Grid container spacing={3}>
          <Grid item xs={12} sm={12}>
            <input
              accept="image/png, image/jpeg"
              type="file"
              id="organization-logo-file"
              style={{display: "none"}}
              disabled={!isEdit}
              onChange={handleLogo}
            />
            <label htmlFor="organization-logo-file" >
            <IconButton color="primary" aria-label="upload picture" component="span">
              <Badge badgeContent={<Icon>edit</Icon>} color="info"
                     anchorOrigin={{
                       vertical: 'top',
                       horizontal: 'right',
                     }}
              invisible={!isEdit}>
              <MDAvatar src={organizationLogo}
                        alt="Organization Logo"
                        size="xxl"
                        shadow="sm"
                        onChange={(e)=>setOrganizationLogo(e.target.value)}
             />
              </Badge>
            </IconButton>
            </label>
          </Grid>
          <Grid item xs={12} sm={12}>
            <FormField label="Organization Name"
                       value={organizationName}
                       disabled={!isEdit}
                       onChange={(e)=>setOrganizationName(e.target.value)}
            />
          </Grid>
          <Grid item xs={12}>
            <MDBox mb={1} ml={0.5} lineHeight={0} display="inline-block">
              <MDTypography component="label" variant="button" fontWeight="regular" color="text">
                Description&nbsp;&nbsp;
                <MDTypography variant="caption" color="text">
                  (optional)
                </MDTypography>
              </MDTypography>
            </MDBox>
            <MDEditor value={description} onChange={setDescription}
                      readOnly={!isEdit}
            />

          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Facebook"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <FacebookIcon />
                  </InputAdornment>
                ),
              }}
              value={facebook?facebook:""}
              disabled={!isEdit}
              onChange={(e)=>setFacebook(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Instagram"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Instagram />
                  </InputAdornment>
                ),
              }}
              value={instagram?instagram:""}
              disabled={!isEdit}
              onChange={(e)=>setInstagram(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <TextField
              label="Twitter"
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Twitter />
                  </InputAdornment>
                ),
              }}
              value={twitter?twitter:""}
              disabled={!isEdit}
              onChange={(e)=>setTwitter(e.target.value)}
              fullWidth
            />
          </Grid>
          <Grid item xs={12} sm={6}>
            <MDBox display="flex" flexDirection={{ xs: "column", sm: "row" }}>
              <MDBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
              <MDButton variant="gradient" color={isEdit?"error":"info"} sx={{ height: "100%" }} onClick={handleEditCancel}>
                {!isEdit ? "Edit": "Cancel"}
              </MDButton>
              </MDBox>
              <MDBox ml={{ xs: 0, sm: 1 }} mt={{ xs: 1, sm: 0 }}>
              <MDButton variant="gradient" color="success"
                        sx={{ height: "100%" }}
                        disabled={!isEdit}
                        onClick={handleSave}
              >
                Save
              </MDButton>
            </MDBox>
          </MDBox>
        </Grid>

        </Grid>
      </MDBox>
    </Card>
  );
}

export default OrganizationInfo;
